////// Lib. //////
//////////////////

import { gsap } from "gsap";

///// Assets /////
//////////////////

const SCROLL_KEYS = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "PageUp", "PageDown", "Home", "End", " ", "Spacebar"]);
const EDITABLE_SELECTOR = "input, textarea, select, [contenteditable]";

// Scroller class //
//////////////////

/**
 * Scrolls an opened project row into a comfortable reading position, clearing
 * the sticky tab bar. Owned by ProjectViewerV2, which calls scrollToItem() on open.
 */
export default class ProjectScroller {
  // Setup //
  //////////////////

  constructor({ offsetEl = null, gapUnits = 1, duration = 0.7, ease = "power2.inOut", tolerance = 2, lockScroll = true, onScrollStart = null, onScrollEnd = null } = {}) {
    this._offsetEl = offsetEl;
    this._gapUnits = gapUnits;
    this._duration = duration;
    this._ease = ease;
    this._tolerance = tolerance;
    this._lockScroll = lockScroll;
    this._onScrollStart = onScrollStart;
    this._onScrollEnd = onScrollEnd;

    this._tween = null;
    this._onInterrupt = null;
    this._locked = false;
    this._onBlockScroll = null;
    this._onBlockKeys = null;
    this._restoreY = null;
    this._scrolling = false;
  }

  // Methods //
  //////////////////

  scrollToItem(item) {
    if (!item) return;

    this.cancel();

    // Remembered so restore() can bring the page back here once the item closes.
    this._restoreY = window.scrollY;

    const start = this._restoreY;
    if (Math.abs(this._targetFor(item) - start) < this._tolerance) {
      this.lock();
      return;
    }

    if (this._prefersReducedMotion()) {
      window.scrollTo(0, this._targetFor(item));
      this.lock();
      return;
    }

    this._attachInterrupts();
    this._beginScroll();

    const progress = { value: 0 };
    this._tween = gsap.to(progress, {
      value: 1,
      duration: this._duration,
      ease: this._ease,
      onUpdate: () => {
        // Recomputed per frame: the row's resting position keeps moving while a
        // previously open panel above it collapses, so a fixed target would undershoot.
        const target = this._targetFor(item);
        window.scrollTo(0, start + (target - start) * progress.value);
      },
      onComplete: () => {
        this._detachInterrupts();
        this._endScroll();
        this.lock();
      },
    });
  }

  // Scrolls back to the position scrollToItem() was called from — i.e. wherever the
  // item sat on screen right before it was opened. Called on close.
  restore() {
    this.cancel();

    if (this._restoreY === null) return;

    const target = this._restoreY;
    this._restoreY = null;

    const start = window.scrollY;
    if (Math.abs(target - start) < this._tolerance) return;

    if (this._prefersReducedMotion()) {
      window.scrollTo(0, target);
      return;
    }

    this._attachInterrupts();
    this._beginScroll();

    const progress = { value: 0 };
    this._tween = gsap.to(progress, {
      value: 1,
      duration: this._duration,
      ease: this._ease,
      onUpdate: () => window.scrollTo(0, start + (target - start) * progress.value),
      onComplete: () => {
        this._detachInterrupts();
        this._endScroll();
      },
    });
  }

  // Blocks scroll input rather than setting overflow:hidden, so the page keeps its
  // scrollbar and layout, and programmatic scrolling still works while locked.
  // No-ops entirely when lockScroll is disabled (feature still unproven for production).
  lock() {
    if (!this._lockScroll || this._locked) return;
    this._locked = true;

    this._onBlockScroll = (e) => {
      if (e.target instanceof Element && e.target.closest(".project-viewer-v2__info-scroll-content")) return;
      if (e.cancelable) e.preventDefault();
    };

    this._onBlockKeys = (e) => {
      if (!SCROLL_KEYS.has(e.key)) return;
      if (e.target instanceof Element && e.target.closest(EDITABLE_SELECTOR)) return;
      if (e.target instanceof Element && e.target.closest(".project-viewer-v2__info-scroll-content")) return;
      e.preventDefault();
    };

    window.addEventListener("wheel", this._onBlockScroll, { passive: false });
    window.addEventListener("touchmove", this._onBlockScroll, { passive: false });
    window.addEventListener("keydown", this._onBlockKeys);
  }

  unlock() {
    if (!this._locked) return;
    this._locked = false;

    window.removeEventListener("wheel", this._onBlockScroll);
    window.removeEventListener("touchmove", this._onBlockScroll);
    window.removeEventListener("keydown", this._onBlockKeys);

    this._onBlockScroll = null;
    this._onBlockKeys = null;
  }

  cancel() {
    this._tween?.kill();
    this._tween = null;
    this._detachInterrupts();
    this._endScroll();
    this.unlock();
  }

  // Guarantees onScrollEnd always fires exactly once per onScrollStart, even when a
  // tween is interrupted (cancel()) rather than left to finish naturally.
  _beginScroll() {
    if (this._scrolling) return;
    this._scrolling = true;
    this._onScrollStart?.();
  }

  _endScroll() {
    if (!this._scrolling) return;
    this._scrolling = false;
    this._onScrollEnd?.();
  }

  destroy() {
    this.cancel();
    this._offsetEl = null;
  }

  // Internals //
  //////////////////

  _targetFor(item) {
    const row = item.closest(".project-viewer-v2__row") || item;
    const documentTop = row.getBoundingClientRect().top + window.scrollY - 1;
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

    return Math.max(0, Math.min(maxScroll, documentTop - this._offsetPx()));
  }

  // Sticky elements sit at their CSS `top` once stuck, so that plus their height is
  // the amount of viewport they actually occupy.
  _offsetPx() {
    const gap = this._gapUnits * this._unitSpacingPx();
    if (!this._offsetEl) return gap;

    const stickyTop = parseFloat(getComputedStyle(this._offsetEl).top) || 0;
    return stickyTop + this._offsetEl.offsetHeight;
  }

  _unitSpacingPx() {
    const value = getComputedStyle(document.documentElement).getPropertyValue("--unit-spacing");
    return parseFloat(value) || 0;
  }

  _prefersReducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  }

  _attachInterrupts() {
    if (this._onInterrupt) return;

    this._onInterrupt = () => this.cancel();
    window.addEventListener("wheel", this._onInterrupt, { passive: true });
    window.addEventListener("touchstart", this._onInterrupt, { passive: true });
  }

  _detachInterrupts() {
    if (!this._onInterrupt) return;

    window.removeEventListener("wheel", this._onInterrupt);
    window.removeEventListener("touchstart", this._onInterrupt);
    this._onInterrupt = null;
  }
}
